import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../lib/api';

/**
 * Pipeline editor (docs/dev/api-deals.md „Pipelines (M8)", Admin 03/04). Queries under `['organization']`
 * (realtime `organization.updated { section: 'pipelines' }`).
 */

export type PipelineType = 'property' | 'financing' | 'lifecycle';

export interface ActionState {
  allowed: boolean;
  reason: string | null;
}

export interface PipelineItem {
  id: string;
  number: number;
  name: string;
  condition: string | null;
  system: boolean;
  dealCount: number;
  actions: { rename: ActionState; move: ActionState; delete: ActionState };
}

export interface PipelineEditor {
  tabs: { key: PipelineType; label: string; active: boolean }[];
  type: PipelineType;
  pipelineId: string | null;
  editable: boolean;
  items: PipelineItem[];
  actions: { addStage: ActionState };
}

export interface StageDeletion {
  stage: { id: string; name: string; dealCount: number };
  deals: { id: string; number: string; contactShortName: string }[];
  targets: { id: string; name: string }[];
  defaultTargetId: string | null;
}

export const pipelineKeys = {
  editor: (type: PipelineType) => ['organization', 'pipelines', type] as const,
  deletion: (stageId: string) => ['organization', 'pipelines', 'deletion', stageId] as const,
};

export function usePipelineEditor(type: PipelineType) {
  return useQuery({
    queryKey: pipelineKeys.editor(type),
    queryFn: () => api<PipelineEditor>(`/settings/organization/pipelines?type=${type}`),
    retry: false,
  });
}

export function useStageDeletion(stageId: string | null) {
  return useQuery({
    queryKey: pipelineKeys.deletion(stageId ?? ''),
    queryFn: () => api<StageDeletion>(`/settings/organization/stages/${stageId}/deletion`),
    enabled: stageId !== null,
    staleTime: 0,
    retry: false,
  });
}

function useStoreEditor() {
  const queryClient = useQueryClient();
  return (editor: PipelineEditor) => {
    queryClient.setQueryData(pipelineKeys.editor(editor.type), editor);
    // Deal boards and records name the stages too.
    void queryClient.invalidateQueries({ queryKey: ['deals'] });
    void queryClient.invalidateQueries({ queryKey: ['deal'] });
  };
}

export function useAddStage() {
  const store = useStoreEditor();
  return useMutation({
    mutationFn: ({
      pipelineId,
      name,
      afterStageId,
    }: {
      pipelineId: string;
      name: string;
      afterStageId: string;
    }) =>
      api<PipelineEditor>(`/settings/organization/pipelines/${pipelineId}/stages`, {
        method: 'POST',
        body: { name, afterStageId },
      }),
    onSuccess: store,
  });
}

export function useRenameStage() {
  const store = useStoreEditor();
  return useMutation({
    mutationFn: ({ stageId, name }: { stageId: string; name: string }) =>
      api<PipelineEditor>(`/settings/organization/stages/${stageId}`, { method: 'PATCH', body: { name } }),
    onSuccess: store,
  });
}

export function useReorderStages() {
  const store = useStoreEditor();
  return useMutation({
    mutationFn: ({ pipelineId, stageIds }: { pipelineId: string; stageIds: string[] }) =>
      api<PipelineEditor>(`/settings/organization/pipelines/${pipelineId}/order`, {
        method: 'PUT',
        body: { stageIds },
      }),
    onSuccess: store,
  });
}

export function useDeleteStage() {
  const store = useStoreEditor();
  return useMutation({
    mutationFn: ({ stageId, targetStageId }: { stageId: string; targetStageId: string | null }) =>
      api<PipelineEditor>(`/settings/organization/stages/${stageId}`, {
        method: 'DELETE',
        body: targetStageId ? { targetStageId } : {},
      }),
    onSuccess: store,
  });
}
